import { Constants } from "@eagerdog/constants";
import { IEventRegisterRow } from "../../../services/helper.service.js";
import { IShow } from "@eagerdog/interfaces";

/**
 * CKC Conformation entry form (CKC Conformation Show Rules, Chapter 12 and 13).
 *
 * Regular classes (12.2.1) are a single dropdown: a dog enters one regular class per show.
 * Non-regular (12.6.1), special (12.6.1(c)), sweepstakes (12.8.3) and altered (Chapter 13)
 * classes are multi-select checkboxes. Every section is filtered by the classes the club
 * offers at this show (`show.show_elements`); an empty list shows everything.
 */
export namespace CKCConformation {
  export const getForm = (show?: IShow): IEventRegisterRow[] => {
    const isLevelAvailable = (level: string, parentElement: string): boolean => {
      if (!show?.show_elements || show.show_elements.length === 0) {
        return true;
      }

      const matchingParents = show.show_elements.filter(se => se.show_element === parentElement);

      if (matchingParents.length === 0) {
        return false;
      }

      return matchingParents.some(parentSe => {
        if (!parentSe.level || parentSe.level === '' || parentSe.level === 'None') {
          return true;
        }
        return parentSe.level === level;
      });
    };

    const toOption = (level: string) => ({ value: level, id: level });

    const regularOptions = [
      Constants.dog_class_element_level.ckc_junior_puppy,
      Constants.dog_class_element_level.ckc_senior_puppy,
      Constants.dog_class_element_level.ckc_12_18_month,
      Constants.dog_class_element_level.ckc_canadian_bred,
      Constants.dog_class_element_level.ckc_bred_by_exhibitor,
      Constants.dog_class_element_level.ckc_open,
      Constants.dog_class_element_level.ckc_specials_only,
    ].map(toOption).filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation));

    const nonRegularOptions = [
      Constants.dog_class_element_level.ckc_baby_puppy,
      Constants.dog_class_element_level.ckc_veterans,
      Constants.dog_class_element_level.ckc_exhibition_only,
    ].map(toOption).filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_non_regular));

    const specialOptions = [
      Constants.dog_class_element_level.ckc_brace,
    ].map(toOption).filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_special_classes));

    const sweepstakesOptions = [
      Constants.dog_class_element_level.ckc_sweepstakes_4_6,
      Constants.dog_class_element_level.ckc_sweepstakes_6_9,
      Constants.dog_class_element_level.ckc_sweepstakes_9_12,
      Constants.dog_class_element_level.ckc_sweepstakes_12_18,
      Constants.dog_class_element_level.ckc_veterans_sweepstakes,
    ].map(toOption).filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_sweepstakes));

    const alteredOptions = [
      Constants.dog_class_element_level.ckc_altered,
    ].map(toOption).filter(opt => isLevelAvailable(opt.value, Constants.dog_class_element.conformation_altered));

    const formRows: IEventRegisterRow[] = [];

    // Row 1: Regular class dropdown (one per show)
    if (regularOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "dropdown",
          flex: 2,
          id: Constants.dog_class_element.conformation,
          label: Constants.dog_class_element.conformation,
          options: [{ value: "None", id: "None" }, ...regularOptions]
        }]
      });
    }

    // Row 2: Non-Regular classes
    if (nonRegularOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_non_regular,
          multiSelect: true,
          options: nonRegularOptions
        }]
      });
    }

    // Row 3: Sweepstakes (dog must also be in a regular class, Baby Puppy or Exhibition Only - 12.8.1)
    if (sweepstakesOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_sweepstakes,
          multiSelect: true,
          options: sweepstakesOptions
        }]
      });
    }

    // Row 4: Special classes
    if (specialOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_special_classes,
          multiSelect: true,
          options: specialOptions
        }]
      });
    }

    // Row 5: Altered
    if (alteredOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [{
          type: "checkbox",
          label: Constants.dog_class_element.conformation_altered,
          multiSelect: true,
          options: alteredOptions
        }]
      });
    }

    return formRows;
  };

  // Backward compatibility
  export const form: IEventRegisterRow[] = getForm();
}
